// src/services/inheritance.service.js
import CryptoJS from 'crypto-js';
import { getUserCredentials } from './api.service';
import { CONTENT_ENDPOINTS, STREAM_ENDPOINTS, DELETE_ENDPOINTS, INHERITANCE_ENDPOINTS } from '../utils/constants';

const ENCRYPTION_KEY = 'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2';

/**
 * Get token from session storage
 * @returns {string} Token
 */
const getToken = () => {
  const credentials = getUserCredentials();
  return credentials ? credentials.token : sessionStorage.getItem('userToken');
};

/**
 * Get user email from session storage
 * @returns {string} User email
 */
const getUserEmail = () => {
  const credentials = getUserCredentials();
  if (credentials) return credentials.email;
  
  const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
  return userDetails.Email || '';
};

/**
 * Get the list of inheritances
 * @returns {Promise<Array>} List of inheritances
 */
export const getInheritanceList = async () => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bucket: 'inheritance' }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result || !result.filesMetadata) {
      return [];
    }
    
    // Transform data to a more usable format
    return result.filesMetadata.map((file, index) => ({
      id: index + 1,
      name: file.Key,
      size: file.Size,
      lastModified: file.LastModified
    }));
  } catch (error) {
    console.error('Error fetching inheritance list:', error);
    throw error;
  }
};

/**
 * Get inheritance details
 * @param {string} inheritanceName - Inheritance name
 * @returns {Promise<Object>} Inheritance details
 */
export const getInheritance = async (inheritanceName) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(
      STREAM_ENDPOINTS.FILE,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucket: 'inheritance',
          fileName: inheritanceName,
        }),
        credentials: 'include',
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const text = await response.text();
    return parseInheritanceContent(text);
  } catch (error) {
    console.error('Error fetching inheritance:', error);
    throw error;
  }
};

/**
 * Parse inheritance content from text format
 * @param {string} content - Inheritance content
 * @returns {Object} Parsed inheritance
 */
const parseInheritanceContent = (content) => {
  const lines = content.split('\n');
  const data = {};
  
  lines.forEach(line => {
    const separatorIndex = line.indexOf(': ');
    if (separatorIndex !== -1) {
      const key = line.substring(0, separatorIndex).trim();
      const value = line.substring(separatorIndex + 2).trim();
      data[key] = value;
    }
  });
  
  // Decrypt selected items if available
  if (data['Selected Items']) {
    try {
      const decryptedItems = CryptoJS.AES.decrypt(
        data['Selected Items'],
        ENCRYPTION_KEY
      ).toString(CryptoJS.enc.Utf8);
      
      // Safely parse JSON with error handling
      try {
        // First check if decrypted string is valid
        if (decryptedItems && decryptedItems.trim()) {
          data.selectedItems = JSON.parse(decryptedItems);
        } else {
          console.warn('Decrypted items string is empty');
          data.selectedItems = { 
            files: [], 
            passwords: [], 
            banking: [], 
            blockchain: [] 
          };
        }
      } catch (parseError) {
        console.error('Error parsing selected items JSON:', parseError);
        // Provide default object in case of parse error
        data.selectedItems = { 
          files: [], 
          passwords: [], 
          banking: [], 
          blockchain: [] 
        };
      }
    } catch (decryptError) {
      console.error('Error decrypting selected items:', decryptError);
      data.selectedItems = { 
        files: [], 
        passwords: [], 
        banking: [], 
        blockchain: [] 
      };
    }
  } else {
    // Default empty structure if no selected items
    data.selectedItems = { 
      files: [], 
      passwords: [], 
      banking: [], 
      blockchain: [] 
    };
  }
  
  return {
    alias: data['Alias'] || '',
    account: data['Inheritance Account'] || '',
    duration: data['Duration'] || '',
    selectedItems: data.selectedItems
  };
};

/**
 * Add a file to inheritance
 * @param {string} fileName - File name or path
 * @param {string} inheritanceName - Inheritance name
 * @returns {Promise<boolean>} Success status
 */
export const addFileToInheritance = async (fileName, inheritanceName) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    // First, get the existing inheritance data
    const inheritance = await getInheritance(inheritanceName);
    
    // Check if the file is already in the inheritance
    if (inheritance.selectedItems.files.includes(fileName)) {
      throw new Error('File is already added to this endowment');
    }
    
    // Add the file to the selected items
    inheritance.selectedItems.files.push(fileName);
    
    // Encrypt the updated selected items
    const encryptedSelectedItems = CryptoJS.AES.encrypt(
      JSON.stringify(inheritance.selectedItems),
      ENCRYPTION_KEY
    ).toString();
    
    // Delete existing inheritance
    await deleteInheritance(inheritanceName);
    
    // Create updated inheritance
    const alias = inheritanceName.endsWith('.txt') 
      ? inheritanceName.slice(0, -4) 
      : inheritanceName;
      
    const email = getUserEmail();
    
    const response = await fetch(
      INHERITANCE_ENDPOINTS.UPLOAD,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          alias: alias,
          account: inheritance.account,
          selectedItems: encryptedSelectedItems,
          duration: inheritance.duration,
          Email: email
        }),
        credentials: 'include',
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error adding file to inheritance:', error);
    throw error;
  }
};

/**
 * Delete an inheritance
 * @param {string} inheritanceName - Inheritance name
 * @returns {Promise<boolean>} Success status
 */
export const deleteInheritance = async (inheritanceName) => {
  const token = getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  try {
    const response = await fetch(
      DELETE_ENDPOINTS.FILE,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucket: 'inheritance',
          fileName: inheritanceName,
        }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting inheritance:', error);
    throw error;
  }
};

/**
 * Update inheritance after file deletion
 * @param {string} deletedFileName - Deleted file name
 * @returns {Promise<boolean>} Success status
 */
export const updateInheritancesAfterFileDeletion = async (deletedFileName) => {
  try {
    const token = getToken();
    const inheritances = await getInheritanceList();
    
    if (inheritances.length === 0) {
      return true;
    }
    
    for (const inheritance of inheritances) {
      try {
        const inheritanceData = await getInheritance(inheritance.name);
        const fileIndex = inheritanceData.selectedItems.files.indexOf(deletedFileName);
        
        if (fileIndex !== -1) {
          // Remove the file from the inheritance
          inheritanceData.selectedItems.files.splice(fileIndex, 1);
          
          // Encrypt the updated selected items
          const encryptedSelectedItems = CryptoJS.AES.encrypt(
            JSON.stringify(inheritanceData.selectedItems),
            ENCRYPTION_KEY
          ).toString();
          
          // Delete the existing inheritance
          await deleteInheritance(inheritance.name);
          
          // Create updated inheritance
          const alias = inheritance.name.endsWith('.txt') 
            ? inheritance.name.slice(0, -4) 
            : inheritance.name;
            
          const email = getUserEmail();
          
          await fetch(
            INHERITANCE_ENDPOINTS.UPLOAD,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                alias: alias,
                account: inheritanceData.account,
                selectedItems: encryptedSelectedItems,
                duration: inheritanceData.duration,
                Email: email
              }),
              credentials: 'include',
            }
          );
        }
      } catch (error) {
        console.error(`Error processing inheritance ${inheritance.name}:`, error);
        continue;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error updating inheritances after file deletion:', error);
    throw error;
  }
};

/**
 * Remove TXT extension from inheritance name
 * @param {string} name - Inheritance name
 * @returns {string} Name without extension
 */
export const removeTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr.slice(0, -4);
  }
  return nameStr;
};

/**
 * Ensure inheritance name has TXT extension
 * @param {string} name - Inheritance name
 * @returns {string} Name with extension
 */
export const ensureTxtExtension = (name) => {
  if (!name) return '';
  const nameStr = String(name);
  if (nameStr.endsWith && nameStr.endsWith('.txt')) {
    return nameStr;
  }
  return nameStr + '.txt';
};

/**
 * Check if recipient email exists in the system
 * @param {Object} data - The data object containing the email to check
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the check
 */
export const checkRecipientEmail = async (data, token) => {
  const { email, creatorEmail } = data;
  
  if (!email || !creatorEmail) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      `${INHERITANCE_ENDPOINTS.CHECK_RECIPIENT_EMAIL}?email=${encodeURIComponent(email)}&creatorEmail=${encodeURIComponent(creatorEmail)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking recipient email:', error);
    throw error;
  }
};

/**
 * Get inheritance status
 * @param {Object} data - The data object containing creator and recipient emails
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Status of the inheritance
 */
export const getInheritanceStatus = async (data, token) => {
  const { creatorEmail, recipientEmail } = data;
  
  if (!creatorEmail || !recipientEmail) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.STATUS,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          creatorEmail,
          recipientEmail
        })
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting inheritance status:', error);
    throw error;
  }
};

/**
 * Get duration for inheritance
 * @param {Object} data - The data object containing creator and recipient emails
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Duration details
 */
export const getDuration = async (data, token) => {
  const { creatorEmail, recipientEmail } = data;
  
  if (!creatorEmail) {
    throw new Error('Missing creator email');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.DURATION,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          creatorEmail,
          recipientEmail: recipientEmail || ''
        })
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting duration:', error);
    throw error;
  }
};

/**
 * Upload a new inheritance
 * @param {Object} data - Inheritance data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the upload operation
 */
export const uploadInheritance = async (data, token) => {
  const { alias, account, selectedItems, duration, Email } = data;
  
  if (!alias || !account || !selectedItems || !duration || !Email) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.UPLOAD,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error uploading inheritance:', error);
    throw error;
  }
};

/**
 * Edit an existing inheritance
 * @param {Object} data - Inheritance data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the edit operation
 */
export const editInheritance = async (data, token) => {
  const { 
    oldAlias, 
    newAlias, 
    oldRecipientEmail, 
    newRecipientEmail, 
    selectedItems, 
    duration, 
    Email 
  } = data;
  
  if (!oldAlias || !newAlias || !oldRecipientEmail || !newRecipientEmail || 
      !selectedItems || !duration || !Email) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.EDIT,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error editing inheritance:', error);
    throw error;
  }
};

/**
 * Resend a rejected inheritance
 * @param {Object} data - Inheritance data
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Result of the resend operation
 */
export const resendInheritance = async (data, token) => {
  const { inheritanceId, creatorEmail } = data;
  
  if (!inheritanceId || !creatorEmail) {
    throw new Error('Missing required fields');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.RESEND,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error resending inheritance:', error);
    throw error;
  }
};

/**
 * Check inheritance availability
 * @param {Object} data - The data object with inheritance ID
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} Availability status
 */
export const checkInheritanceAvailability = async (inheritanceId, token) => {
  if (!inheritanceId) {
    throw new Error('Missing inheritance ID');
  }
  
  try {
    const response = await fetch(
      INHERITANCE_ENDPOINTS.CHECK_AVAILABILITY,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ inheritanceId })
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking inheritance availability:', error);
    throw error;
  }
};